import { useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserMenu from '@/components/feature/UserMenu';
import AuthModal from '@/components/feature/AuthModal';
import LanguageSelector from '@/components/feature/LanguageSelector';
import LogoBackground from '@/components/feature/LogoBackground';
import NavNotificationBell from '@/components/feature/NavNotificationBell';
import GlobalSearch from '@/components/feature/GlobalSearch';
import CartDrawer from '@/components/feature/CartDrawer';
import { useCart } from '@/contexts/CartContext';

interface SiteLayoutProps {
  children: ReactNode;
}

interface RippleItem {
  id: number;
  x: number;
  y: number;
  size: number;
}

export default function SiteLayout({ children }: SiteLayoutProps) {
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navHeight, setNavHeight] = useState(80);
  const [ripples, setRipples] = useState<RippleItem[]>([]);
  const location = useLocation();
  const { t } = useTranslation('common');
  const navRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { count: cartCount, openCart } = useCart();

  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    setScrolled(y > 40);
    setScrollProgress(docH > 0 ? Math.min((y / docH) * 100, 100) : 0);
    setIsScrolling(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 350);
    // Tab bar contrai no scroll
    setNavHeight(y > 40 ? 60 : 80);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [handleScroll]);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const addRipple = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const id = Date.now();
    setRipples(prev => [...prev, { id, x, y, size }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 700);
  }, []);

  const navItems: { href: string; label: string; icon: string; isAnchor?: boolean }[] = [
    { href: '/', label: t('nav_home'), icon: 'ri-home-4-line' },
    { href: '/livros', label: t('nav_books'), icon: 'ri-book-2-line' },
    { href: '/cursos', label: t('nav_courses'), icon: 'ri-play-circle-line' },
    { href: '/mentoria', label: t('nav_mentorship'), icon: 'ri-user-star-line' },
    { href: '/webinars', label: t('nav_webinars'), icon: 'ri-live-line' },
    { href: '/sobre', label: t('nav_about'), icon: 'ri-building-line' },
    { href: '/contato', label: t('nav_contact'), icon: 'ri-customer-service-line' },
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href.split('#')[0]) && href.split('#')[0] !== '/';
  };

  return (
    <div className="relative min-h-screen bg-[#0A0A0F]">
      <LogoBackground />

      {/* ── NAVBAR FLOATING GLASS ── */}
      <nav
        ref={navRef}
        translate="no"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'nav-glass-floating' : 'bg-transparent'
        }`}
        style={{ height: `${navHeight}px` }}
      >
        {/* Scroll Progress Bar */}
        {scrolled && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D4AF37]/60 via-[#F0D060] to-[#D4AF37]/60 transition-all duration-150"
              style={{ width: `${scrollProgress}%` }}
            />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div
            className="flex items-center justify-between h-full transition-all duration-500"
          >
            {/* Logo */}
            <Link to="/" className="flex items-center cursor-pointer group shrink-0">
              <img
                src="/images/logotipo.jpg"
                alt="SB Family & Mastery"
                className="w-auto transition-all duration-500 group-hover:scale-105"
                style={{ height: `${isScrolling || scrolled ? 36 : 44}px` }}
              />
            </Link>

            {/* Desktop Nav Links */}
            <div
              className={`hidden md:flex items-center gap-1 transition-all duration-500 ${
                isScrolling ? 'opacity-80 scale-95' : 'opacity-100 scale-100'
              }`}
            >
              {navItems.map((item) => {
                const active = isActive(item.href);
                if (item.isAnchor) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 overflow-hidden group
                        ${active
                          ? 'text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/25'
                          : 'text-gray-300 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.08]'
                        }`}
                      onClick={addRipple}
                    >
                      {ripples.map(r => (
                        <span
                          key={r.id}
                          className="ripple-gold"
                          style={{ left: r.x, top: r.y, width: r.size, height: r.size }}
                        />
                      ))}
                      {item.label}
                    </a>
                  );
                }
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 overflow-hidden
                      ${active
                        ? 'text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/25'
                        : 'text-gray-300 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.08]'
                      }`}
                  >
                    {active && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#D4AF37] rounded-full" />
                    )}
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Right Actions */}
            <div className="hidden md:flex items-center gap-3 shrink-0">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-gray-400 hover:text-white transition-all cursor-pointer"
                title="Buscar (Ctrl+K)"
              >
                <i className="ri-search-line text-base"></i>
                <span className="text-xs text-gray-500 hidden lg:inline">Buscar...</span>
                <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[9px] text-gray-600">⌘K</kbd>
              </button>
              {/* Cart Button */}
              <button
                onClick={openCart}
                className="relative w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-gray-400 hover:text-[#D4AF37] transition-all cursor-pointer"
                title="Meu Carrinho"
              >
                <i className="ri-shopping-cart-2-line text-base"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-[#D4AF37] rounded-full text-[#0A0A0F] text-[9px] font-bold">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
              <LanguageSelector variant="light" size="compact" />
              <div className="w-px h-5 bg-white/12" />
              <NavNotificationBell />
              <UserMenu />
            </div>

            {/* Mobile Menu Toggle */}
            <div className="md:hidden flex items-center gap-2">
              {/* Mobile Cart Button */}
              <button
                onClick={openCart}
                className="relative w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-gray-400 cursor-pointer transition-all"
              >
                <i className="ri-shopping-cart-2-line text-base"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-[#D4AF37] rounded-full text-[#0A0A0F] text-[9px] font-bold">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
              <button
                className="relative w-10 h-10 flex items-center justify-center rounded-xl glass text-gray-300 cursor-pointer transition-all duration-300 hover:text-[#D4AF37] overflow-hidden"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Abrir menu"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── MOBILE OVERLAY ── */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[60] md:hidden"
          style={{ backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.6)' }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ── MOBILE DRAWER ── */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-[70] flex flex-col transition-transform duration-500 md:hidden`}
        style={{
          backdropFilter: 'blur(40px) saturate(280%) brightness(1.04)',
          WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.04)',
          background: 'linear-gradient(160deg, rgba(12,12,22,0.97) 0%, rgba(8,8,16,0.94) 100%)',
          borderLeft: '1px solid rgba(212,175,55,0.2)',
          boxShadow: '-8px 0 48px rgba(0,0,0,0.65), -1px 0 0 rgba(255,255,255,0.05)',
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: '1px solid rgba(212,175,55,0.15)' }}>
          <img
            src="/images/logotipo.jpg"
            alt="SB Family & Mastery"
            className="h-10 w-auto"
          />
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-white cursor-pointer transition-colors"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Drawer Links */}
        <nav className="flex flex-col px-4 py-5 gap-1 flex-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = isActive(item.href);
            if (item.isAnchor) {
              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer ${
                    active
                      ? 'text-[#D4AF37] border border-[#D4AF37]/25'
                      : 'text-gray-300 hover:text-[#D4AF37]'
                  }`}
                  style={active ? { background: 'rgba(212,175,55,0.1)' } : {}}
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-base`}></i>
                  </span>
                  {item.label}
                </a>
              );
            }
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer ${
                  active
                    ? 'text-[#D4AF37] border border-[#D4AF37]/25'
                    : 'text-gray-300 hover:text-[#D4AF37]'
                }`}
                style={active ? { background: 'rgba(212,175,55,0.1)' } : {}}
              >
                <span className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base`}></i>
                </span>
                {item.label}
              </Link>
            );
          })}

          <div className="mt-4 px-1 flex flex-col gap-2">
            {/* Mobile Search */}
            <button
              onClick={() => { setMobileMenuOpen(false); setSearchOpen(true); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white transition-colors cursor-pointer w-full"
            >
              <span className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-search-line text-base"></i>
              </span>
              Buscar conteúdo
            </button>
          </div>

          <div className="mt-4 px-1 flex items-center gap-2">
            <NavNotificationBell />
          </div>
          <div className="mt-3 px-1">
            <UserMenu />
          </div>
          <div className="mt-3 px-1">
            <LanguageSelector variant="light" size="full" />
          </div>
        </nav>

        {/* Drawer Footer */}
        <div className="px-6 py-4" style={{ borderTop: '1px solid rgba(212,175,55,0.12)' }}>
          {/* Ano calculado — havia um segundo "© 2025" literal aqui, no rodapé
              do menu lateral, que envelhecia junto com o do rodapé principal. */}
          <p className="text-xs text-gray-600 text-center">
            SB Family &amp; Mastery © {new Date().getFullYear()}
          </p>
        </div>
      </div>

      {/* ── CONTEÚDO ── */}
      <div className="relative z-10 pt-20">
        {children}
      </div>

      <AuthModal />
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <CartDrawer />
    </div>
  );
}
